import React from 'react';
import { useTranslation } from 'react-i18next';
import translations from '../lib/translations';
import { translate } from '../lib/i18n/translate';
import { resolveLocale } from '../lib/i18n/resolveLocale';

export interface TProps {
  value: string;
  dangerousHTML?: boolean;
  tag?: string | React.ComponentType<any>;
  className?: string;
  style?: React.CSSProperties;
  [replacement: string]: any;
}

// Drop-in replacement for react-redux-i18n's `<Translate as T>` (ECDEX-3440):
// same props, same key lookup/pluralization/`%{var}` interpolation via
// `translate()`. `@international/mastodon-i18n`'s `i18n.setLocale()` already
// calls `i18next.changeLanguage()` internally, so `useTranslation()` here
// re-renders on every real locale switch today — no new wiring needed,
// no Redux, no bespoke Context. Only `i18n.language` is used from it; the
// string itself still comes from `translate()`, not i18next's own `t()`,
// because these translation keys use `%{var}` placeholders, not i18next's
// `{{var}}` syntax.
const T: React.FC<TProps> = props => {
  const { value, tag, dangerousHTML, style, className } = props;
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const Tag = tag ?? 'span';

  // Mirrors react-i18nify's `otherProps()`: every prop except `value` is
  // passed through as a possible `%{token}` replacement, `count` included.
  const { value: _value, ...replacements } = props;
  const translation = translate(translations, locale, value, replacements);

  if (dangerousHTML) {
    return (
      <Tag
        style={style}
        className={className}
        dangerouslySetInnerHTML={{ __html: translation }}
      />
    );
  }
  return (
    <Tag style={style} className={className}>
      {translation}
    </Tag>
  );
};

export default T;
